import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback } from "react";
import { errorToast, successToast } from "../../../_components/context/ToastProvider";
import { useAuthenticatedContext } from "../../../_components/context/AuthenticatedContext";
import { FetchResult } from "@apollo/client/core";
import {
  OptimisticDreamSearchItemFragmentDoc,
  OptimisticDreamSearchItemFragment,
  ToggleFavoriteEnrollmentItemForDreamSearchMutation,
  GetFavoritedDreamSearchItemsDocument,
  ToggleFavoriteEnrollmentItemForDreamSearchDocument,
} from "./generated";
import { gql } from "graphql-tag";

import { useApolloClient } from "@apollo/client/react";
import { useApolloCache } from "../../../_components/apolloUtils";
import { DreamSearchViewerResult } from "@portal/app/_common/types/dreamSearch";
import { useEcommerceEvents } from "@portal/app/_components/hooks/useEcommerceEvents";

void gql`
  query getFavoritedDreamSearchItems {
    getFavoritedDreamSearchItems {
      ...DreamSearchItemFragment
    }
  }
  fragment OptimisticDreamSearchItem on DreamSearchItem {
    id
    favorited
  }
  mutation toggleFavoriteEnrollmentItemForDreamSearch($customerId: ID!, $programId: ID!) {
    toggleFavoriteEnrollmentItem(programId: $programId, customerId: $customerId) {
      id
      programId
      status
      provider {
        id
        fullName
      }
      program {
        id
        name
        vocation {
          id
          adjective
        }
      }
    }
  }
`;

type FavoriteItem = {
  id: string;
  provider: {
    id: string;
    fullName: string;
  };
  program: {
    id: string;
    name: string;
    vocation: {
      id: string;
      adjective: string;
    };
  };
};

export type ToggleFavorite = (
  item: FavoriteItem
) =>
  | Promise<FetchResult<ToggleFavoriteEnrollmentItemForDreamSearchMutation>>
  | Promise<void>
  | void;
export type ToggleFavoriteCallback = (item: DreamSearchViewerResult) => Promise<void> | void;
export const useToggleFavorite = (): ToggleFavorite => {
  const { loggedIn, customerId, openSignInModal } = useAuthenticatedContext();
  const { evictRootQueries } = useApolloCache();
  const cache = useApolloClient().cache;
  const [toggleFavoriteEnrollmentItemForDreamSearch] = useMutation(
    ToggleFavoriteEnrollmentItemForDreamSearchDocument
  );
  const { trackAddToWishlist } = useEcommerceEvents();
  /**
   * Calling this ensures that the cache.readFragment call below finds the
   * favorited item in the cache.
   */
  useQuery(GetFavoritedDreamSearchItemsDocument, { skip: !customerId });

  return useCallback(
    (item: FavoriteItem) => {
      if (!loggedIn) {
        openSignInModal("CREATE_ACCOUNT");
        return;
      }
      if (!customerId) {
        errorToast("Log in as a student to favorite a class");
        return;
      }
      const dreamSearchItem = cache.readFragment<OptimisticDreamSearchItemFragment>({
        id: cache.identify({ id: item.id, __typename: "DreamSearchItem" }),
        fragment: OptimisticDreamSearchItemFragmentDoc,
      });

      return toggleFavoriteEnrollmentItemForDreamSearch({
        variables: {
          programId: item.program.id,
          customerId,
        },
        optimisticResponse: !dreamSearchItem?.favorited
          ? {
              toggleFavoriteEnrollmentItem: {
                __typename: "EnrollmentItem",
                id: "new-cached-ei",
                programId: item.program.id,
                status: "FAVORITE",
                provider: item.provider,
                program: item.program,
              },
            }
          : { toggleFavoriteEnrollmentItem: null },
        onError: () => {
          errorToast("Failed to favorite item. Please try again.");
        },
        onCompleted: async ({ toggleFavoriteEnrollmentItem }) => {
          if (toggleFavoriteEnrollmentItem) {
            trackAddToWishlist(toggleFavoriteEnrollmentItem);
            successToast("Saved to your favorites.");
          }
          evictRootQueries(["getFavoritedDreamSearchItems", "me", "viewerInProgressEnrollments"]);
        },
        update(cache, result, requestContext) {
          if (requestContext.variables) {
            cache.modify({
              id: cache.identify({
                id: requestContext.variables.programId,
                __typename: "DreamSearchItem",
              }),
              fields: {
                favorited() {
                  return result.data?.toggleFavoriteEnrollmentItem?.status === "FAVORITE";
                },
              },
            });
          }
        },
      });
    },
    [
      cache,
      customerId,
      evictRootQueries,
      loggedIn,
      openSignInModal,
      toggleFavoriteEnrollmentItemForDreamSearch,
      trackAddToWishlist,
    ]
  );
};
